import {
  BellRing, FileCheck2, Boxes, BarChart3, Users, PenLine, ShieldCheck, Truck, Wallet,
  Gift, Star, CalendarClock, Receipt, MessageCircle, Award, Building2, Phone, Mail,
  Twitter, Instagram, Facebook, Linkedin, Youtube, Send, Globe, Link as LinkIcon, Music2,
} from "lucide-react";

export const LANG_KEYS = ["ar", "en", "ur"];
export const LANG_LABELS = { ar: "عربي", en: "English", ur: "اردو" };

// Icons selectable for feature cards
export const FEATURE_ICON_NAMES = [
  "BellRing", "FileCheck2", "Boxes", "BarChart3", "Users", "PenLine", "ShieldCheck",
  "Truck", "Wallet", "Gift", "Star", "CalendarClock", "Receipt", "MessageCircle", "Award", "Building2",
];

export const ICON_MAP = {
  BellRing, FileCheck2, Boxes, BarChart3, Users, PenLine, ShieldCheck, Truck, Wallet,
  Gift, Star, CalendarClock, Receipt, MessageCircle, Award, Building2, Phone, Mail,
  Twitter, Instagram, Facebook, Linkedin, Youtube, Send, Globe, Link: LinkIcon, Music2,
};

// Contact channel presets
export const CONTACT_TYPES = [
  { type: "whatsapp", label: "واتساب", icon: "MessageCircle", color: "#25D366", link: (v) => `https://wa.me/${(v || "").replace(/[^0-9]/g, "")}` },
  { type: "phone", label: "هاتف", icon: "Phone", color: "#0ea5e9", link: (v) => `tel:${v}` },
  { type: "email", label: "بريد إلكتروني", icon: "Mail", color: "#ef4444", link: (v) => `mailto:${v}` },
  { type: "twitter", label: "تويتر / X", icon: "Twitter", color: "#0f172a", link: (v) => v },
  { type: "instagram", label: "انستقرام", icon: "Instagram", color: "#e1306c", link: (v) => v },
  { type: "facebook", label: "فيسبوك", icon: "Facebook", color: "#1877f2", link: (v) => v },
  { type: "linkedin", label: "لينكدإن", icon: "Linkedin", color: "#0a66c2", link: (v) => v },
  { type: "youtube", label: "يوتيوب", icon: "Youtube", color: "#ff0000", link: (v) => v },
  { type: "tiktok", label: "تيك توك", icon: "Music2", color: "#0f172a", link: (v) => v },
  { type: "telegram", label: "تيليجرام", icon: "Send", color: "#229ED9", link: (v) => v },
  { type: "website", label: "الموقع الإلكتروني", icon: "Globe", color: "#334155", link: (v) => v },
  { type: "custom", label: "رابط مخصص", icon: "Link", color: "#334155", link: (v) => v },
];

export const contactPreset = (type) => CONTACT_TYPES.find((c) => c.type === type) || CONTACT_TYPES[CONTACT_TYPES.length - 1];

// ---- Editable landing extras: contact cards, footer columns/links, info pages ----
export const DEFAULT_CONTACT_HEADING = "تواصل مع فريق ورك زون";
export const DEFAULT_CONTACT_SUB = "فريقنا المتخصص جاهز لمساعدتك في أتمتة عملياتك وتنظيم أسطولك ودعمك على مدار الساعة.";

export const DEFAULT_CARDS = [
  { cat: "مبيعات المؤسسات", title: "استفسارات المبيعات والعروض", email: "sales@workzone.sa", extra: ["+966 11 800 9000"], action: "راسل مبيعاتنا" },
  { cat: "الدعم الفني والتشغيلي", title: "دعم الأسطول والعمليات الميدانية", email: "support@workzone.sa", extra: ["الرياض والمجمعة", "جدة والمنطقة الغربية", "الدمام والشرقية"], action: "تواصل مع الدعم" },
  { cat: "استشارات التحول الرقمي", title: "الحسابات الكبرى والدعم الخاص", email: "vip@workzone.sa", extra: ["+966 50 123 4567"], action: "احجز جلسة استشارية" },
];

export const DEFAULT_FOOTER_TAGLINE = "منصة إدارة العمليات والأسطول والموارد البشرية المتكاملة، مصمّمة خصيصاً للمؤسسات باللغة العربية.";

export const DEFAULT_SOCIAL = { linkedin: "", twitter: "", facebook: "", youtube: "" };

export const DEFAULT_FOOTER_COLS = [
  { h: "الخدمات والحلول", links: [
    { label: "إدارة أسطول المركبات", slug: "fleet" }, { label: "تتبع العهد والمخازن", slug: "custody" },
    { label: "الحضور والرواتب", slug: "attendance" }, { label: "إدارة السائقين والصيانة", slug: "drivers" },
    { label: "تحليلات الأداء", slug: "analytics" }] },
  { h: "عن ورك زون", links: [
    { label: "عن الشركة", slug: "about" }, { label: "لماذا ورك زون؟", slug: "why" },
    { label: "قصص النجاح", slug: "success" }, { label: "الوظائف", slug: "careers" },
    { label: "الأخبار والتحديثات", slug: "news" }] },
  { h: "الموارد", links: [
    { label: "المركز المعرفي", slug: "kb" }, { label: "توثيق API", slug: "api-docs" },
    { label: "حاسبة التكلفة", slug: "calculator" }, { label: "الأسئلة الشائعة", slug: "faq" },
    { label: "حالة النظام", slug: "status" }] },
];

const _page = (slug, title, body) => ({ slug, title, body });
export const DEFAULT_PAGES = [
  _page("fleet", "إدارة أسطول المركبات", "أدر أسطول مركباتك بالكامل: تتبّع اللوحات، ربط السائقين، جدولة الصيانة، ومتابعة استهلاك الوقود من مكان واحد."),
  _page("custody", "تتبع العهد والمخازن", "سجّل العهد المسلّمة للموظفين وأرصدة المخزون وقطع الغيار، مع سندات تسليم واستلام موقّعة."),
  _page("attendance", "الحضور والرواتب", "احسب ساعات الحضور والانصراف والإجازات والسُّلَف والرواتب تلقائياً مع كشوف واضحة."),
  _page("drivers", "إدارة السائقين والصيانة", "تابع أداء السائقين، مواعيد الصيانة الدورية وتغيير الزيت، والمخالفات المرورية."),
  _page("analytics", "تحليلات الأداء", "لوحات ومؤشرات لحظية لأداء المناديب والفروع مع تقارير قابلة للتصدير."),
  _page("about", "عن الشركة", "ورك زون منصة سعودية متكاملة لإدارة العمليات والأسطول والموارد البشرية، صُمّمت خصيصاً للمؤسسات باللغة العربية."),
  _page("why", "لماذا ورك زون؟", "لأنها تجمع كل ما تحتاجه إدارة عملياتك في نظام واحد: توثيق ذكي، تنبيهات تلقائية، ودعم عربي على مدار الساعة."),
  _page("success", "قصص النجاح", "قريباً: قصص عملائنا وكيف ساعدتهم ورك زون على تنظيم أساطيلهم ورفع كفاءة فرقهم."),
  _page("careers", "الوظائف", "نبحث دائماً عن المواهب. تواصل معنا عبر البريد للانضمام إلى فريق ورك زون."),
  _page("news", "الأخبار والتحديثات", "تابع آخر مزايا وتحديثات المنصة هنا."),
  _page("kb", "المركز المعرفي", "أدلة ومقالات إرشادية تساعدك على تحقيق أقصى استفادة من ورك زون."),
  _page("api-docs", "توثيق API", "قريباً: توثيق واجهات برمجة التطبيقات (API) للتكامل مع أنظمتك."),
  _page("calculator", "حاسبة التكلفة", "قدّر تكلفة اشتراكك بناءً على عدد الموظفين والفروع والمزايا التي تحتاجها."),
  _page("faq", "الأسئلة الشائعة", "إجابات على أكثر الأسئلة تكراراً حول الاشتراك والمزايا والدعم."),
  _page("status", "حالة النظام", "جميع الأنظمة تعمل بشكل طبيعي. سيتم عرض أي أعمال صيانة مجدولة هنا."),
];

// Pick a localized string; fall back across langs then to provided default
export const pickText = (obj, lang, fb = "") => {
  if (!obj || typeof obj !== "object") return fb;
  const v = (obj[lang] || obj.ar || obj.en || obj.ur || "").trim?.() ?? "";
  return v || fb;
};
